import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Calendar, Clock, CheckCircle, XCircle, Loader2, Settings, Plus, Trash2, Ban, RefreshCw, Image as ImageIcon, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function AdminReservasPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reservas, setReservas] = useState([]);
  const [stats, setStats] = useState({});
  const [config, setConfig] = useState(null);
  const [bloqueos, setBloqueos] = useState([]);
  
  // Filtros
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  
  // Modales
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showBloqueoModal, setShowBloqueoModal] = useState(false);
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [reservaSeleccionada, setReservaSeleccionada] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  
  // Estado de config temporal
  const [configTemp, setConfigTemp] = useState(null);
  const [bloqueoTemp, setBloqueoTemp] = useState({
    fecha_inicio: '',
    fecha_fin: '',
    hora_inicio: '',
    hora_fin: '',
    motivo: ''
  });

  useEffect(() => {
    fetchData();
  }, [token, filtroEstado, filtroFechaDesde, filtroFechaHasta]);

  const fetchData = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      let url = `${API_URL}/reservas-espacio/admin/reservas?`;
      if (filtroEstado && filtroEstado !== 'todos') url += `estado=${filtroEstado}&`;
      if (filtroFechaDesde) url += `fecha_desde=${filtroFechaDesde}&`;
      if (filtroFechaHasta) url += `fecha_hasta=${filtroFechaHasta}&`;
      
      const [reservasRes, configRes, bloqueosRes] = await Promise.all([
        axios.get(url, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/reservas-espacio/admin/configuracion`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/reservas-espacio/admin/bloqueos`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setReservas(reservasRes.data.reservas);
      setStats(reservasRes.data.stats);
      setConfig(configRes.data);
      setConfigTemp(configRes.data);
      setBloqueos(bloqueosRes.data);
    } catch (err) {
      console.error('Error cargando datos:', err);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async (reservaId) => {
    try {
      await axios.put(`${API_URL}/reservas-espacio/admin/reservas/${reservaId}/estado`, 
        { estado: 'aprobada' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reserva aprobada');
      fetchData();
    } catch (err) {
      toast.error('Error al aprobar');
    }
  };

  const handleRechazar = async () => {
    if (!reservaSeleccionada) return;
    
    try {
      await axios.put(`${API_URL}/reservas-espacio/admin/reservas/${reservaSeleccionada}/estado`, 
        { estado: 'rechazada', motivo_rechazo: motivoRechazo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reserva rechazada');
      setShowRechazoModal(false);
      setMotivoRechazo('');
      setReservaSeleccionada(null);
      fetchData();
    } catch (err) {
      toast.error('Error al rechazar');
    }
  };

  const handleCancelar = async (reservaId) => {
    if (!window.confirm('¿Cancelar esta reserva?')) return;
    
    try {
      await axios.put(`${API_URL}/reservas-espacio/admin/reservas/${reservaId}/estado`, 
        { estado: 'cancelada' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reserva cancelada');
      fetchData();
    } catch (err) {
      toast.error('Error al cancelar');
    }
  };

  const handleToggleReservasAbiertas = async () => {
    const nuevoEstado = !config?.reservas_abiertas;
    try {
      await axios.put(`${API_URL}/reservas-espacio/admin/configuracion`, 
        { ...config, reservas_abiertas: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setConfig({ ...config, reservas_abiertas: nuevoEstado });
      toast.success(nuevoEstado ? 'Reservas abiertas' : 'Reservas cerradas');
    } catch (err) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleGuardarConfig = async () => {
    try {
      await axios.put(`${API_URL}/reservas-espacio/admin/configuracion`, configTemp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada');
      setShowConfigModal(false);
      setConfig(configTemp);
    } catch (err) {
      toast.error('Error al guardar');
    }
  };

  const handleCrearBloqueo = async () => {
    try {
      await axios.post(`${API_URL}/reservas-espacio/admin/bloqueos`, bloqueoTemp, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloqueo creado');
      setShowBloqueoModal(false);
      setBloqueoTemp({ fecha_inicio: '', fecha_fin: '', hora_inicio: '', hora_fin: '', motivo: '' });
      fetchData();
    } catch (err) {
      toast.error('Error al crear bloqueo');
    }
  };

  const handleEliminarBloqueo = async (bloqueoId) => {
    try {
      await axios.delete(`${API_URL}/reservas-espacio/admin/bloqueos/${bloqueoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bloqueo eliminado');
      fetchData();
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  const handleActualizarHorario = (index, campo, valor) => {
    const nuevosHorarios = [...configTemp.horarios];
    nuevosHorarios[index] = { ...nuevosHorarios[index], [campo]: valor };
    setConfigTemp({ ...configTemp, horarios: nuevosHorarios });
  };

  const formatearFecha = (fecha) => {
    try {
      const [year, month, day] = fecha.split('-');
      return `${day}/${month}/${year}`;
    } catch {
      return fecha;
    }
  };

  const getEstadoBadge = (estado) => {
    const estilos = {
      pendiente: 'bg-amber-100 text-amber-800',
      aprobada: 'bg-green-100 text-green-800',
      rechazada: 'bg-red-100 text-red-800',
      cancelada: 'bg-gray-100 text-gray-600'
    };
    return <Badge className={estilos[estado]}>{estado.charAt(0).toUpperCase() + estado.slice(1)}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold">Gestión de Reservas</h1>
            <p className="text-gray-600">{config?.nombre || 'Espacio del Colegio'}</p>
          </div>
          {/* Badge de estado */}
          <Badge 
            className={`${config?.reservas_abiertas !== false ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} cursor-pointer`}
            onClick={handleToggleReservasAbiertas}
            data-testid="badge-estado-reservas"
          >
            {config?.reservas_abiertas !== false ? (
              <><CheckCircle className="w-3.5 h-3.5 mr-1" /> Reservas abiertas</>
            ) : (
              <><XCircle className="w-3.5 h-3.5 mr-1" /> Cerrado momentáneamente</>
            )}
          </Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={config?.reservas_abiertas !== false ? "outline" : "default"}
            onClick={handleToggleReservasAbiertas}
            data-testid="btn-toggle-reservas"
            className={config?.reservas_abiertas !== false ? "" : "bg-green-600 hover:bg-green-700"}
          >
            {config?.reservas_abiertas !== false ? (
              <><XCircle className="w-4 h-4 mr-2" />Cerrar Reservas</>
            ) : (
              <><CheckCircle className="w-4 h-4 mr-2" />Abrir Reservas</>
            )}
          </Button>
          <Button variant="outline" onClick={() => setShowBloqueoModal(true)} data-testid="btn-nuevo-bloqueo">
            <Ban className="w-4 h-4 mr-2" />
            Bloquear Fechas
          </Button>
          <Button variant="outline" onClick={() => setShowConfigModal(true)} data-testid="btn-configuracion">
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4 text-center">
            <div className="text-3xl font-bold text-amber-600">{stats.pendientes || 0}</div>
            <div className="text-sm text-amber-700">Pendientes</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-4 text-center">
            <div className="text-3xl font-bold text-green-600">{stats.aprobadas || 0}</div>
            <div className="text-sm text-green-700">Aprobadas</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="py-4 text-center">
            <div className="text-3xl font-bold text-red-600">{stats.rechazadas || 0}</div>
            <div className="text-sm text-red-700">Rechazadas</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="py-4 text-center">
            <div className="text-3xl font-bold text-gray-600">{stats.canceladas || 0}</div>
            <div className="text-sm text-gray-700">Canceladas</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="reservas">
        <TabsList>
          <TabsTrigger value="reservas">Reservas</TabsTrigger>
          <TabsTrigger value="bloqueos">Bloqueos ({bloqueos.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="reservas" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                    <SelectTrigger className="w-40" data-testid="filtro-estado">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="aprobada">Aprobada</SelectItem>
                      <SelectItem value="rechazada">Rechazada</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input 
                    type="date" 
                    value={filtroFechaDesde} 
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                    className="w-40"
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input 
                    type="date" 
                    value={filtroFechaHasta} 
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button variant="ghost" size="icon" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de reservas */}
          <Card>
            <CardContent className="p-0">
              {reservas.length > 0 ? (
                <div className="divide-y">
                  {reservas.map(reserva => (
                    <div 
                      key={reserva.id}
                      className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 hover:bg-gray-50"
                      data-testid={`reserva-admin-${reserva.id}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{reserva.usuario_nombre || reserva.usuario_email}</span>
                          {reserva.usuario_matricula && (
                            <Badge variant="outline">Mat. {reserva.usuario_matricula}</Badge>
                          )}
                          {getEstadoBadge(reserva.estado)}
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatearFecha(reserva.fecha)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {reserva.hora}hs
                          </span>
                        </div>
                        {reserva.motivo && (
                          <p className="text-sm text-gray-500">📝 {reserva.motivo}</p>
                        )}
                        {reserva.motivo_rechazo && (
                          <p className="text-sm text-red-600">❌ {reserva.motivo_rechazo}</p>
                        )}
                      </div>
                      
                      {reserva.estado === 'pendiente' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleAprobar(reserva.id)}
                            data-testid={`aprobar-${reserva.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setReservaSeleccionada(reserva.id);
                              setShowRechazoModal(true);
                            }}
                            data-testid={`rechazar-${reserva.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                      
                      {reserva.estado === 'aprobada' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600"
                          onClick={() => handleCancelar(reserva.id)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay reservas con los filtros seleccionados
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bloqueos">
          <Card>
            <CardContent className="p-4">
              {bloqueos.length > 0 ? (
                <div className="space-y-3">
                  {bloqueos.map(bloqueo => (
                    <div 
                      key={bloqueo.id}
                      className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-red-800">
                          {formatearFecha(bloqueo.fecha_inicio)}
                          {bloqueo.fecha_fin !== bloqueo.fecha_inicio && ` - ${formatearFecha(bloqueo.fecha_fin)}`}
                        </div>
                        {bloqueo.hora_inicio && (
                          <div className="text-sm text-red-600">
                            {bloqueo.hora_inicio} - {bloqueo.hora_fin}
                          </div>
                        )}
                        {bloqueo.motivo && (
                          <div className="text-sm text-red-700 mt-1">{bloqueo.motivo}</div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-100"
                        onClick={() => handleEliminarBloqueo(bloqueo.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay bloqueos activos
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Configuración */}
      <Dialog open={showConfigModal} onOpenChange={setShowConfigModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configuración del Espacio</DialogTitle>
          </DialogHeader>
          
          {configTemp && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del espacio</Label>
                  <Input
                    value={configTemp.nombre || ''}
                    onChange={(e) => setConfigTemp({ ...configTemp, nombre: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duración del turno (minutos)</Label>
                  <Input
                    type="number"
                    value={configTemp.duracion_turno_minutos || 60}
                    onChange={(e) => setConfigTemp({ ...configTemp, duracion_turno_minutos: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              
              <div>
                <Label>Descripción</Label>
                <Textarea
                  value={configTemp.descripcion || ''}
                  onChange={(e) => setConfigTemp({ ...configTemp, descripcion: e.target.value })}
                  rows={2}
                />
              </div>

              <HeaderImagenConfig
                config={configTemp}
                onChange={(parche) => setConfigTemp((prev) => ({ ...prev, ...parche }))}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Anticipación mínima (horas)</Label>
                  <Input
                    type="number"
                    value={configTemp.anticipacion_minima_horas || 24}
                    onChange={(e) => setConfigTemp({ ...configTemp, anticipacion_minima_horas: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Anticipación máxima (días)</Label>
                  <Input
                    type="number"
                    value={configTemp.anticipacion_maxima_dias || 30}
                    onChange={(e) => setConfigTemp({ ...configTemp, anticipacion_maxima_dias: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Máx. reservas activas por usuario</Label>
                  <Input
                    type="number"
                    value={configTemp.max_reservas_activas_por_usuario || 3}
                    onChange={(e) => setConfigTemp({ ...configTemp, max_reservas_activas_por_usuario: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Máx. reservas por día por usuario</Label>
                  <Input
                    type="number"
                    value={configTemp.max_reservas_por_dia_por_usuario || 2}
                    onChange={(e) => setConfigTemp({ ...configTemp, max_reservas_por_dia_por_usuario: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>Requiere aprobación manual</Label>
                  <p className="text-sm text-gray-500">Las reservas quedan pendientes hasta aprobarlas</p>
                </div>
                <Switch
                  checked={configTemp.requiere_aprobacion !== false}
                  onCheckedChange={(checked) => setConfigTemp({ ...configTemp, requiere_aprobacion: checked })}
                />
              </div>
              
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label>Solo matrícula habilitada</Label>
                  <p className="text-sm text-gray-500">Solo matriculados con estado "activa" pueden reservar</p>
                </div>
                <Switch
                  checked={configTemp.solo_matricula_habilitada !== false}
                  onCheckedChange={(checked) => setConfigTemp({ ...configTemp, solo_matricula_habilitada: checked })}
                />
              </div>
              
              <div>
                <Label className="block mb-2">Horarios por día de la semana</Label>
                <div className="space-y-2">
                  {configTemp.horarios?.map((horario, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <span className="w-24 font-medium">{DIAS_SEMANA[horario.dia_semana]}</span>
                      <Input
                        type="time"
                        value={horario.hora_inicio}
                        onChange={(e) => handleActualizarHorario(idx, 'hora_inicio', e.target.value)}
                        className="w-28"
                        disabled={!horario.activo}
                      />
                      <span>a</span>
                      <Input
                        type="time"
                        value={horario.hora_fin}
                        onChange={(e) => handleActualizarHorario(idx, 'hora_fin', e.target.value)}
                        className="w-28"
                        disabled={!horario.activo}
                      />
                      <Switch
                        checked={horario.activo}
                        onCheckedChange={(checked) => handleActualizarHorario(idx, 'activo', checked)}
                      />
                      <span className="text-sm text-gray-500">{horario.activo ? 'Activo' : 'Inactivo'}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigModal(false)}>Cancelar</Button>
            <Button onClick={handleGuardarConfig}>Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Nuevo Bloqueo */}
      <Dialog open={showBloqueoModal} onOpenChange={setShowBloqueoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bloquear Fechas</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fecha inicio</Label>
                <Input
                  type="date"
                  value={bloqueoTemp.fecha_inicio}
                  onChange={(e) => setBloqueoTemp({ ...bloqueoTemp, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label>Fecha fin (opcional)</Label>
                <Input
                  type="date"
                  value={bloqueoTemp.fecha_fin}
                  onChange={(e) => setBloqueoTemp({ ...bloqueoTemp, fecha_fin: e.target.value })}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Hora inicio (opcional)</Label>
                <Input
                  type="time"
                  value={bloqueoTemp.hora_inicio}
                  onChange={(e) => setBloqueoTemp({ ...bloqueoTemp, hora_inicio: e.target.value })}
                  placeholder="Dejar vacío para todo el día"
                />
              </div>
              <div>
                <Label>Hora fin</Label>
                <Input
                  type="time"
                  value={bloqueoTemp.hora_fin}
                  onChange={(e) => setBloqueoTemp({ ...bloqueoTemp, hora_fin: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Motivo</Label>
              <Textarea
                value={bloqueoTemp.motivo}
                onChange={(e) => setBloqueoTemp({ ...bloqueoTemp, motivo: e.target.value })}
                placeholder="Ej: Feriado, mantenimiento, evento especial..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBloqueoModal(false)}>Cancelar</Button>
            <Button onClick={handleCrearBloqueo} disabled={!bloqueoTemp.fecha_inicio}>
              Crear bloqueo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Rechazo */}
      <Dialog open={showRechazoModal} onOpenChange={setShowRechazoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Reserva</DialogTitle>
          </DialogHeader>
          
          <div>
            <Label>Motivo del rechazo (opcional)</Label>
            <Textarea
              value={motivoRechazo}
              onChange={(e) => setMotivoRechazo(e.target.value)}
              placeholder="Ej: Espacio no disponible por mantenimiento..."
              rows={3}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowRechazoModal(false);
              setMotivoRechazo('');
            }}>Cancelar</Button>
            <Button variant="destructive" onClick={handleRechazar}>
              Confirmar rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function HeaderImagenConfig({ config, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = React.useRef(null);

  const url = config.header_imagen_url || '';
  const x = Number(config.header_position_x ?? 50);
  const y = Number(config.header_position_y ?? 50);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Subí un archivo de imagen (JPG, PNG, WebP).');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('La imagen no puede superar 8 MB.');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/reservas-espacio/admin/configuracion/header-imagen`,
        fd,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      onChange({ header_imagen_url: res.data.header_imagen_url });
      toast.success('Imagen del header actualizada');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo subir la imagen.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleQuitar = async () => {
    if (!confirm('¿Quitar la imagen del header?')) return;
    setUploading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/reservas-espacio/admin/configuracion/header-imagen`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange({ header_imagen_url: null });
      toast.success('Imagen del header eliminada');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo eliminar la imagen.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="header-imagen-config">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-4 h-4 text-violet-600" />
        <Label className="text-sm font-semibold">Imagen del header (banner)</Label>
      </div>
      <p className="text-xs text-gray-500">
        Aparece como banner arriba del título en la página pública de reservas. Proporción 3:1 (ancho:alto).
      </p>

      {/* Preview */}
      <div
        className="w-full bg-gray-200 rounded-md overflow-hidden border"
        style={{ aspectRatio: '3 / 1' }}
      >
        {url ? (
          <img
            src={url}
            alt="header"
            className="w-full h-full object-cover"
            style={{ objectPosition: `${x}% ${y}%` }}
            data-testid="header-preview-img"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
            Sin imagen — subí una para ver la previsualización
          </div>
        )}
      </div>

      {/* Controles de foco — solo si hay imagen */}
      {url && (
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div>
            <Label className="text-xs text-gray-600">Posición horizontal: {x}%</Label>
            <input
              type="range"
              min={0}
              max={100}
              value={x}
              onChange={(e) => onChange({ header_position_x: Number(e.target.value) })}
              className="w-full accent-violet-600"
              data-testid="header-pos-x"
            />
          </div>
          <div>
            <Label className="text-xs text-gray-600">Posición vertical: {y}%</Label>
            <input
              type="range"
              min={0}
              max={100}
              value={y}
              onChange={(e) => onChange({ header_position_y: Number(e.target.value) })}
              className="w-full accent-violet-600"
              data-testid="header-pos-y"
            />
          </div>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="header-imagen-subir"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />}
          {url ? 'Cambiar imagen' : 'Subir imagen'}
        </Button>
        {url && (
          <Button type="button" variant="ghost" size="sm" onClick={handleQuitar} disabled={uploading} data-testid="header-imagen-quitar">
            <Trash2 className="w-4 h-4 mr-1.5" />
            Quitar
          </Button>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Los controles de posición se aplican al instante en la previsualización. Igual recordá apretar <strong>Guardar</strong> abajo para persistirlos.
      </p>
      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>
      )}
    </div>
  );
}
